import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CarWithContextModel,
  RequestQuickAddSliceModel,
} from "./requestQuickAddSliceModel";
import { GraphQLClient } from "../../../../shared/gql-client";
import { GraphQLReadResponse } from "../../../general/domain/models/graphql";
import { GET_CAR_WITH_CONTEXT } from "../../../car/infrastructure/queries/carQueries";
import { toast } from "sonner";
import { gql } from "@apollo/client";
import { GET_USER_RELATIONS } from "../../../relation/infrastructure/queries/RelationQueries";
import { GET_PICKUP_DELIVERY_DATES } from "../queries/requestQueries";
import { CAR_QUICK_ADD_SEARCH_QUERY } from "../queries/carQuickAddSearchQuery";
import i18next from "i18next";

export const getCarWithContext = createAsyncThunk(
  "requestQuickAdd/car/get",
  async (id: string, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    try {
      const response = (await client.query(
        "carWithContext",
        GET_CAR_WITH_CONTEXT,
        {
          id: parseInt(id),
        },
      )) as GraphQLReadResponse;

      return response as unknown as CarWithContextModel;
    } catch (err: unknown) {
      // Extract meaningful error message from GraphQL error
      let errorMessage =
        "An unexpected error occurred while fetching car data.";

      if (typeof err === "object" && err !== null && "graphQLErrors" in err) {
        const gqlErrors = (
          err as {
            graphQLErrors?: { message: string }[];
          }
        ).graphQLErrors;
        if (Array.isArray(gqlErrors) && gqlErrors.length > 0) {
          errorMessage = gqlErrors[0].message;
        }
      } else if (
        typeof err === "object" &&
        err !== null &&
        "networkError" in err
      ) {
        const netErrors = (
          err as {
            networkError?: {
              result?: {
                errors?: { message: string }[];
              };
            };
          }
        ).networkError?.result?.errors;
        if (Array.isArray(netErrors) && netErrors.length > 0) {
          errorMessage = netErrors[0].message;
        }
      } else if (typeof err === "object" && err !== null && "message" in err) {
        errorMessage = (err as { message: string }).message;
      }

      return thunkAPI.rejectWithValue({
        message: errorMessage,
        originalError: err,
      });
    }
  },
);

export const createRequest = createAsyncThunk(
  "requestQuickAdd/request/create",
  async (
    requestData: {
      // Common fields
      type: string;
      work_order_number: string | null;
      pickup_and_delivery_date: string;
      relation_id: string;
      // For existing cars
      tireset_id?: string;
      // For new cars
      licence?: string;
      tireset_type?: string;
      car_id?: string;
    },
    thunkAPI,
  ) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    try {
      const mutation = `
        mutation CreateRequestWithCarAndTireset(
          $type: String!
          $work_order_number: String
          $pickup_and_delivery_date: Date!
          $relation_id: ID!
          $tireset_id: ID
          $licence: String
          $tireset_type: String
          $car_id: ID
        ) {
          createRequestWithCarAndTireset(
            type: $type
            work_order_number: $work_order_number
            pickup_and_delivery_date: $pickup_and_delivery_date
            relation_id: $relation_id
            tireset_id: $tireset_id
            licence: $licence
            tireset_type: $tireset_type
            car_id: $car_id
          ) {
            id
            type
            work_order_number
            pickup_and_delivery_date
            state
            source_type
            source_id
          }
        }
      `;

      const variables = {
        type: requestData.type,
        work_order_number: requestData.work_order_number || null,
        pickup_and_delivery_date: requestData.pickup_and_delivery_date,
        relation_id: requestData.relation_id,
        tireset_id: requestData.tireset_id || null,
        licence: requestData.licence || null,
        tireset_type: requestData.tireset_type || null,
        car_id: requestData.car_id || null,
      };

      return await client.mutate(
        "createRequestWithCarAndTireset",
        gql`
          ${mutation}
        `,
        variables,
      );
    } catch (err: unknown) {
      // Extract meaningful error message from GraphQL error
      let errorMessage =
        "An unexpected error occurred while creating the request.";

      if (typeof err === "object" && err !== null && "graphQLErrors" in err) {
        const gqlErrors = (
          err as {
            graphQLErrors?: { message: string }[];
          }
        ).graphQLErrors;
        if (Array.isArray(gqlErrors) && gqlErrors.length > 0) {
          errorMessage = gqlErrors[0].message;
        }
      } else if (
        typeof err === "object" &&
        err !== null &&
        "networkError" in err
      ) {
        const netErrors = (
          err as {
            networkError?: {
              result?: {
                errors?: unknown[];
              };
            };
          }
        ).networkError?.result?.errors;
        if (Array.isArray(netErrors) && netErrors.length > 0) {
          errorMessage = netErrors[0].message;
        }
      } else if (typeof err === "object" && err !== null && "message" in err) {
        errorMessage = (err as { message: string }).message;
      }

      return thunkAPI.rejectWithValue({
        message: errorMessage,
        originalError: err,
      });
    }
  },
);

export const updateRequest = createAsyncThunk(
  "requestQuickAdd/request/update",
  async (
    requestData: {
      id: number;
      work_order_number: string | null;
      pickup_and_delivery_date: string;
      type: string;
    },
    thunkAPI,
  ) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    try {
      const mutation = `
        mutation UpsertRequest(
          $id: ID
          $work_order_number: String
          $pickup_and_delivery_date: Date
          $type: String!
        ) {
          upsertRequest(
            id: $id
            work_order_number: $work_order_number
            pickup_and_delivery_date: $pickup_and_delivery_date
            type: $type
          ) {
            id
            work_order_number
            pickup_and_delivery_date
            type
            state
          }
        }
      `;

      // Use the type from the request data
      const variables = {
        id: requestData.id,
        work_order_number: requestData.work_order_number,
        pickup_and_delivery_date: requestData.pickup_and_delivery_date,
        type: requestData.type,
      };

      return await client.mutate(
        "upsertRequest",
        gql`
          ${mutation}
        `,
        variables,
      );
    } catch (err: unknown) {
      // Extract meaningful error message from GraphQL error
      let errorMessage =
        "An unexpected error occurred while updating the request.";

      if (
        typeof err === "object" &&
        err !== null &&
        err !== undefined &&
        "graphQLErrors" in err
      ) {
        const gqlErrors = (
          err as {
            graphQLErrors?: { message: string }[];
          }
        ).graphQLErrors;
        if (Array.isArray(gqlErrors) && gqlErrors.length > 0) {
          errorMessage = gqlErrors[0].message;
        }
      } else if (
        typeof err === "object" &&
        err !== null &&
        err !== undefined &&
        "networkError" in err &&
        typeof (err as { networkError?: unknown }).networkError === "object"
      ) {
        const netErrors = (
          err as { networkError?: { result?: { errors?: unknown[] } } }
        ).networkError?.result?.errors;
        if (Array.isArray(netErrors) && netErrors.length > 0) {
          errorMessage = netErrors[0].message;
        }
      } else if (typeof err === "object" && err !== null && "message" in err) {
        errorMessage = (err as { message: string }).message;
      }

      return thunkAPI.rejectWithValue({
        message: errorMessage,
        originalError: err,
      });
    }
  },
);

export const fetchUserRelations = createAsyncThunk(
  "requestQuickAdd/fetchUserRelations",
  async (_, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    try {
      const response = (await client.query(
        "relations",
        GET_USER_RELATIONS,
        {},
      )) as GraphQLReadResponse;
      // Expecting response as { userRelations: [{ id, name }] }
      return (response.data || []) as { id: string; name: string }[];
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const fetchPickupDeliveryDates = createAsyncThunk<
  { firstDate: string; forbiddenDates: string[] } | null,
  void,
  { extra: { client: GraphQLClient } }
>("requestQuickAdd/fetchPickupDeliveryDates", async (_, thunkAPI) => {
  const { client } = thunkAPI.extra;
  try {
    const response = (await client.query(
      "getPickupDeliveryDates",
      GET_PICKUP_DELIVERY_DATES,
      {},
    )) as {
      firstDate: string;
      forbiddenDates: string[];
    };

    if (response && Array.isArray(response.forbiddenDates)) {
      return response;
    }
    return null;
  } catch (err) {
    return thunkAPI.rejectWithValue(err);
  }
});

export interface CarQuickAddOption {
  id: string;
  licence: string | null;
  additional_info: string | null;
  freestock_tire?: boolean;
}

export const fetchCarQuickAddOptions = createAsyncThunk<
  CarQuickAddOption[],
  string,
  { extra: { client: GraphQLClient } }
>(
  "requestQuickAdd/fetchCarQuickAddOptions",
  async (search: string, thunkAPI) => {
    const { client } = thunkAPI.extra;
    try {
      const response = (await client.query("cars", CAR_QUICK_ADD_SEARCH_QUERY, {
        search: `%${search}%`,
      })) as GraphQLReadResponse;
      return (response?.data || []) as CarQuickAddOption[];
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

const initialState = {
  loading: false,
  car: null,
  relations: [],
  pickupDeliveryDates: null,
  carQuickAddOptions: [],
} as RequestQuickAddSliceModel;

const requestQuickAddSlice = createSlice({
  name: "requestQuickAdd",
  initialState,
  reducers: {
    clearCarWithContext: (state) => {
      state.car = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCarWithContext.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getCarWithContext.fulfilled,
      (state, action: PayloadAction<CarWithContextModel | null>) => {
        state.loading = false;
        state.car = action.payload;
      },
    );
    builder.addCase(updateRequest.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateRequest.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createRequest.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createRequest.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(fetchUserRelations.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      fetchUserRelations.fulfilled,
      (state, action: PayloadAction<{ id: string; name: string }[]>) => {
        state.loading = false;
        state.relations = action.payload;
      },
    );
    builder.addCase(fetchUserRelations.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(fetchPickupDeliveryDates.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchPickupDeliveryDates.fulfilled, (state, action) => {
      state.loading = false;
      state.pickupDeliveryDates = action.payload || null;
    });
    builder.addCase(fetchPickupDeliveryDates.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(fetchCarQuickAddOptions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchCarQuickAddOptions.fulfilled, (state, action) => {
      state.loading = false;
      state.carQuickAddOptions = action.payload || [];
    });
    builder.addCase(fetchCarQuickAddOptions.rejected, (state) => {
      state.loading = false;
    });
    builder.addMatcher(
      (action) =>
        [
          getCarWithContext.rejected.type,
          updateRequest.rejected.type,
          createRequest.rejected.type,
        ].includes(action.type),
      (
        state,
        action: {
          payload: unknown;
        },
      ) => {
        state.loading = false;

        // Handle both new error format and legacy format
        let errorMessage = "An unexpected error occurred.";
        if (
          typeof action.payload === "object" &&
          action.payload !== null &&
          "message" in action.payload
        ) {
          errorMessage = (action.payload as { message: string }).message;
        } else if (
          typeof action.payload === "object" &&
          action.payload !== null &&
          "errors" in action.payload
        ) {
          const errorsArr = (
            action.payload as {
              errors?: { message: string }[];
            }
          ).errors;
          if (Array.isArray(errorsArr) && errorsArr.length > 0) {
            errorMessage = errorsArr[0].message;
          }
        }

        // Check if errorMessage is a translation key or raw text
        const translatedMessage = errorMessage.includes(".")
          ? i18next.t(errorMessage, { ns: "common" })
          : errorMessage;

        toast.error(translatedMessage);
      },
    );
  },
});

export const { clearCarWithContext } = requestQuickAddSlice.actions;

export default requestQuickAddSlice.reducer;
